/**
 * Customer Portal Knowledge Base Page
 * Browse and search help articles
 */

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import CustomerPortalLayout from '@/components/customer-portal/CustomerPortalLayout';
import { customerPortalService, type KBArticle, type KBCategory } from '@/services/api/customerPortal';
import {
  Search,
  BookOpen,
  FolderOpen,
  ArrowRight,
  ThumbsUp,
  Eye,
  Clock,
  TrendingUp,
  Newspaper,
} from 'lucide-react';

export default function CustomerPortalKnowledgeBasePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';

  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchResults, setSearchResults] = useState<KBArticle[]>([]);
  const [categories, setCategories] = useState<KBCategory[]>([]);
  const [popularArticles, setPopularArticles] = useState<KBArticle[]>([]);
  const [recentArticles, setRecentArticles] = useState<KBArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState<'browse' | 'search'>(initialQuery ? 'search' : 'browse');

  useEffect(() => {
    loadInitialData();
    if (initialQuery) {
      handleSearch(initialQuery);
    }
  }, []);

  const loadInitialData = async () => {
    try {
      const [categoriesData, popularData, recentData] = await Promise.all([
        customerPortalService.getCategories(),
        customerPortalService.getPopularArticles(6),
        customerPortalService.getRecentArticles(6),
      ]);
      setCategories(categoriesData);
      setPopularArticles(popularData);
      setRecentArticles(recentData);
    } catch (error) {
      console.error('Failed to load KB data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setActiveTab('browse');
      searchParams.delete('q');
      setSearchParams(searchParams);
      return;
    }

    setSearching(true);
    setActiveTab('search');
    searchParams.set('q', query);
    setSearchParams(searchParams);

    try {
      const result = await customerPortalService.searchKB(query);
      setSearchResults(result.results);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(searchQuery);
  };

  return (
    <CustomerPortalLayout title="Knowledge Base">
      {/* Search Header */}
      <Card className="mb-8 bg-gradient-to-r from-green-600 to-teal-600 text-white border-0">
        <CardContent className="pt-8 pb-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-2">How can we help?</h2>
            <p className="text-green-100 mb-6">
              Search our knowledge base for answers to common questions.
            </p>
            <form onSubmit={handleSearchSubmit} className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search for articles, guides, tutorials..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-24 py-6 text-gray-900 bg-white border-0 rounded-lg shadow-lg"
              />
              <Button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-v2accent hover:bg-v2accent/90"
              >
                Search
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>

      {/* Search Results */}
      {activeTab === 'search' && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {searching ? 'Searching...' : `Search Results (${searchResults.length})`}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setActiveTab('browse');
                searchParams.delete('q');
                setSearchParams(searchParams);
              }}
            >
              Clear Search
            </Button>
          </div>

          {searching ? (
            <Card>
              <CardContent className="py-12">
                <div className="flex flex-col items-center justify-center">
                  <div className="w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin mb-4" />
                  <p className="text-gray-500">Searching knowledge base...</p>
                </div>
              </CardContent>
            </Card>
          ) : searchResults.length > 0 ? (
            <div className="space-y-4">
              {searchResults.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No results found
                </h4>
                <p className="text-gray-500 mb-4">
                  Try different keywords or browse our categories below.
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchQuery('');
                    setActiveTab('browse');
                  }}
                >
                  Browse Categories
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Browse Mode */}
      {activeTab === 'browse' && (
        <>
          {/* Categories */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <FolderOpen className="w-5 h-5 mr-2 text-green-600" />
              Browse by Category
            </h3>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="pt-6">
                      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : categories.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/customer-portal/knowledge-base/category/${category.slug}`}
                  >
                    <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                              {category.name}
                            </h4>
                            {category.description && (
                              <p className="text-sm text-gray-500 line-clamp-2">
                                {category.description}
                              </p>
                            )}
                          </div>
                          <Badge variant="secondary" className="ml-2">
                            {category.articleCount}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-gray-500">No categories available yet.</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Popular & Recent Articles */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Popular Articles */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-amber-600" />
                Most Popular
              </h3>
              {loading ? (
                <Card>
                  <CardContent className="pt-6 space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="animate-pulse">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2" />
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ) : popularArticles.length > 0 ? (
                <Card>
                  <CardContent className="pt-4 divide-y divide-gray-100 dark:divide-gray-800">
                    {popularArticles.map((article, index) => (
                      <Link
                        key={article.id}
                        to={`/customer-portal/knowledge-base/${article.slug}`}
                        className="flex items-start py-3 hover:bg-gray-50 dark:hover:bg-gray-800 -mx-4 px-4 transition-colors group"
                      >
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-xs font-medium text-amber-700 dark:text-amber-400 mr-3 mt-0.5">
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 dark:text-white group-hover:text-green-600 line-clamp-1">
                            {article.title}
                          </h4>
                          <div className="flex items-center space-x-3 text-xs text-gray-500 mt-1">
                            <span className="flex items-center">
                              <Eye className="w-3 h-3 mr-1" />
                              {article.viewCount} views
                            </span>
                            <span className="flex items-center">
                              <ThumbsUp className="w-3 h-3 mr-1" />
                              {article.helpfulCount} helpful
                            </span>
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-green-600 flex-shrink-0 ml-2" />
                      </Link>
                    ))}
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="py-8 text-center">
                    <p className="text-gray-500">No articles yet.</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Recent Articles */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Newspaper className="w-5 h-5 mr-2 text-blue-600" />
                Recently Updated
              </h3>
              {loading ? (
                <Card>
                  <CardContent className="pt-6 space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="animate-pulse">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2" />
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ) : recentArticles.length > 0 ? (
                <Card>
                  <CardContent className="pt-4 divide-y divide-gray-100 dark:divide-gray-800">
                    {recentArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/customer-portal/knowledge-base/${article.slug}`}
                        className="flex items-start py-3 hover:bg-gray-50 dark:hover:bg-gray-800 -mx-4 px-4 transition-colors group"
                      >
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3 mt-0.5">
                          <BookOpen className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 dark:text-white group-hover:text-green-600 line-clamp-1">
                            {article.title}
                          </h4>
                          <div className="flex items-center space-x-3 text-xs text-gray-500 mt-1">
                            {article.categoryName && (
                              <span>{article.categoryName}</span>
                            )}
                            <span className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {new Date(article.publishedAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-green-600 flex-shrink-0 ml-2" />
                      </Link>
                    ))}
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="py-8 text-center">
                    <p className="text-gray-500">No articles yet.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </>
      )}

      {/* Can't find what you need? */}
      <Card className="mt-8 bg-gray-50 dark:bg-gray-800/50 border-dashed">
        <CardContent className="py-8 text-center">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Can't find what you're looking for?
          </h4>
          <p className="text-gray-500 mb-4">
            Our support team is here to help you with any questions.
          </p>
          <Link to="/customer-portal/tickets/new">
            <Button className="bg-v2accent hover:bg-v2accent/90">
              Submit a Support Ticket
            </Button>
          </Link>
        </CardContent>
      </Card>
    </CustomerPortalLayout>
  );
}

// Article Card Component
function ArticleCard({ article }: { article: KBArticle }) {
  return (
    <Link to={`/customer-portal/knowledge-base/${article.slug}`}>
      <Card className="hover:shadow-lg transition-shadow cursor-pointer">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between mb-2">
            <h4 className="font-semibold text-gray-900 dark:text-white hover:text-green-600">
              {article.title}
            </h4>
            {article.categoryName && (
              <Badge variant="secondary" className="ml-2 flex-shrink-0">
                {article.categoryName}
              </Badge>
            )}
          </div>
          {article.excerpt && (
            <p className="text-sm text-gray-500 line-clamp-2 mb-3">
              {article.excerpt}
            </p>
          )}
          <div className="flex items-center space-x-4 text-xs text-gray-400">
            <span className="flex items-center">
              <Eye className="w-3 h-3 mr-1" />
              {article.viewCount} views
            </span>
            <span className="flex items-center">
              <ThumbsUp className="w-3 h-3 mr-1" />
              {article.helpfulCount} found helpful
            </span>
            <span className="flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              {new Date(article.publishedAt).toLocaleDateString()}
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
